'use client';
import React, { useCallback, useState } from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { ShortInterest, ShortInterestParams } from '@benzinga/quotes-manager';
import { SessionContext } from '@benzinga/session-context';
import { Button } from '@benzinga/core-ui';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { fetchShortInterest } from './Services';

export const ShortInterestCalendarColumnDef = ColumnsDefinition;
export const fetchShortInterestData = fetchShortInterest;

const FETCH_ERROR_MESSAGE = 'Unable to load short interest data. Please try again.';

const toDay = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const iso = DateTime.fromISO(value);
  if (iso.isValid) return iso.toFormat('yyyy-MM-dd');
  const us = DateTime.fromFormat(value, 'MM/dd/yyyy');
  if (us.isValid) return us.toFormat('yyyy-MM-dd');
  return value.slice(0, 10);
};

export const ShortInterestCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  onDataLoad,
}: ICalendarProps) => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<ShortInterest[]>(initialData || []);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [lastFetchParams, setLastFetchParams] = useState<ShortInterestParams | null>(null);
  let dateRange: IDateRange;

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(DateTime.now().minus({ months: 1 }).toJSDate()),
      date_to: formatRequestDate(new Date()),
    };
  }

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData);
      setFetchError(null);
    }
  }, [initialData, calendarData]);

  const fetchData = useCallback(
    (filterParams: ShortInterestParams) => {
      setLoading(true);
      setFetchError(null);
      setLastFetchParams(filterParams);
      fetchShortInterest(session.getSession(), filterParams)
        .then(result => {
          if (result.err) {
            setTableData([]);
            setFetchError(FETCH_ERROR_MESSAGE);
            return;
          }
          setTableData(result.ok);
          onDataLoad && onDataLoad(result.ok);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [session, onDataLoad],
  );

  const retryFetch = useCallback(() => {
    if (lastFetchParams) {
      fetchData(lastFetchParams);
    }
  }, [fetchData, lastFetchParams]);

  const isPreloadedSummary = !!initialTicker && !!initialData?.length;

  const filterSummaryByDate = useCallback(
    (dateFrom?: string | null, dateTo?: string | null) => {
      const base = initialData ?? [];
      const from = toDay(dateFrom);
      const to = toDay(dateTo);
      setFetchError(null);
      if (!from || !to) {
        setTableData(base);
        return;
      }
      setTableData(
        base.filter(row => {
          const day = toDay(row.recordDate);
          return day !== undefined && day >= from && day <= to;
        }),
      );
    },
    [initialData],
  );

  const symbolsMatchPreloaded = useCallback(
    (symbols: ShortInterestParams['symbols']): boolean => {
      const raw = symbols as unknown as string | string[] | undefined;
      const requestedKey = (Array.isArray(raw) ? raw.join(',') : (raw ?? '')).trim().toUpperCase();
      return requestedKey === '' || requestedKey === (initialTicker ?? '').toUpperCase();
    },
    [initialTicker],
  );

  const onFilterChanged = useCallback(
    (params: ShortInterestParams) => {
      if (isPreloadedSummary && symbolsMatchPreloaded(params.symbols)) {
        filterSummaryByDate(params.dateFrom, params.dateTo);
      } else {
        fetchData(params);
      }
    },
    [fetchData, filterSummaryByDate, isPreloadedSummary, symbolsMatchPreloaded],
  );

  const onTableReady = useCallback(() => {
    if (isPreloadedSummary) {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;
      filterSummaryByDate(urlParams?.get('date_from'), urlParams?.get('date_to'));
    } else if (initialData?.length) {
      setTableData(initialData);
      setFetchError(null);
    } else {
      fetchData({
        dateFrom: dateRange.date_from,
        dateTo: dateRange.date_to,
        symbols: initialTicker ? [initialTicker] : undefined,
      });
    }
  }, [
    dateRange.date_from,
    dateRange.date_to,
    fetchData,
    filterSummaryByDate,
    initialData,
    initialTicker,
    isPreloadedSummary,
  ]);

  return (
    <div className="relative">
      {fetchError && (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          role="alert"
        >
          <span>{fetchError}</span>
          <Button onClick={retryFetch} size="sm" type="button" variant="flat-red">
            Retry
          </Button>
        </div>
      )}
      <Layout
        calendar={calendar}
        calendarData={calendarData}
        calendarType={calendarType}
        columnDefs={ColumnsDefinition}
        enableSearch
        hasStockTableStyling={hasStockTableStyling}
        height={height}
        hideFilters={hideFilters}
        initialDateRange={dateRange}
        noRowsOverlayText={fetchError ?? undefined}
        onFilterChanged={onFilterChanged}
        onTableReady={onTableReady}
        tableData={tableData}
        tableLoading={loading}
        theme="modern"
        ticker={initialTicker}
      />
    </div>
  );
};
